// src/shared/api/http.js
import { tokenStorage } from './storage';
import { uiBus } from './uiBus';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
function buildUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return API_BASE ? `${API_BASE}${p}` : p;
}

// ✅ refresh endpoint
const REFRESH_PATH = '/api/v1/auth/refresh';

// ✅ single-flight refresh
let refreshingPromise = null;

// ✅ dedupe (403 spam olmasın diye)
let lastForbiddenAt = 0;
let lastUnauthorizedAt = 0;
const DEDUPE_MS = 1200;

function emitOnce(type, message) {
  const now = Date.now();
  if (type === 'forbidden') {
    if (now - lastForbiddenAt < DEDUPE_MS) return;
    lastForbiddenAt = now;
  }
  if (type === 'unauthorized') {
    if (now - lastUnauthorizedAt < DEDUPE_MS) return;
    lastUnauthorizedAt = now;
  }
  uiBus.emit('toast', { type: 'error', message });
}

function pickMessage(data, status) {
  return (
    (data && typeof data === 'object' && (data.message || data.error)) ||
    (typeof data === 'string' ? data : null) ||
    `HTTP ${status}`
  );
}

async function doRefresh() {
  const tokens = tokenStorage.get();
  const refreshToken = tokens?.refreshToken;
  if (!refreshToken) {
    const e = new Error('No refresh token');
    e.status = 401;
    throw e;
  }

  const url = buildUrl(REFRESH_PATH);

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  const contentType = res.headers.get('content-type') || '';
  const raw = await res.text();
  let data = null;

  if (raw) {
    if (contentType.includes('application/json')) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }
    } else data = raw;
  }

  if (!res.ok) {
    const err = new Error(pickMessage(data, res.status));
    err.status = res.status;
    err.data = data;
    throw err;
  }

  const accessToken = data?.accessToken;
  const newRefreshToken = data?.refreshToken ?? refreshToken;
  if (!accessToken) {
    const err = new Error('Refresh başarılı ama accessToken dönmedi');
    err.status = 500;
    err.data = data;
    throw err;
  }

  tokenStorage.set({
    accessToken,
    refreshToken: newRefreshToken,
    tokenType: data?.tokenType || 'Bearer',
  });

  return accessToken;
}

async function requestOnce(path, { method = 'GET', body, auth = true, headers } = {}) {
  const url = buildUrl(path);

  const h = {
    ...(headers || {}),
    ...(body != null ? { 'Content-Type': 'application/json' } : {}),
  };

  if (auth) {
    const tokens = tokenStorage.get();
    if (tokens?.accessToken) h.Authorization = `Bearer ${tokens.accessToken}`;
  }

  const res = await fetch(url, {
    method,
    headers: h,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return { res, data: null, url };

  const contentType = res.headers.get('content-type') || '';
  const raw = await res.text();

  let data = null;
  if (raw) {
    if (contentType.includes('application/json')) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }
    } else data = raw;
  }

  return { res, data, url };
}

export async function http(path, opts = {}) {
  const { method = 'GET', body, auth = true, headers } = opts;

  // 1) first try
  const first = await requestOnce(path, { method, body, auth, headers });

  if (first.res.ok) return first.data;

  // ✅ 403: refresh yok, direkt UI uyarı + “clean error”
  if (first.res.status === 403) {
    emitOnce('forbidden', 'Yetkiniz yok.');
    const err = new Error('FORBIDDEN');
    err.status = 403;
    err.data = first.data; // istersen bunu da null yapabilirsin
    throw err;
  }

  // ✅ 401 değilse normal error (ama console spam’i azalt)
  if (first.res.status !== 401) {
    // 401/403 dışı hata: burada istersen dev’de logla
    if (process.env.NODE_ENV !== 'production') {
      console.group('❌ API ERROR');
      console.log('URL:', first.url);
      console.log('STATUS:', first.res.status);
      console.log('RESPONSE:', first.data);
      console.groupEnd();
    }

    const msg = pickMessage(first.data, first.res.status);
    const err = new Error(msg);
    err.status = first.res.status;
    err.data = first.data;
    err.errors = first.data?.errors;
    throw err;
  }

  // 2) 401 geldiyse: auth:false ise refresh deneme
  if (!auth) {
    // ✅ public request 401: login’e yönlendirme/oturum mesajı verme
    const err = new Error('Unauthorized (public)');
    err.status = 401;
    err.data = first.data;
    throw err;
  }

  // 3) refresh endpoint 401 dönerse loop yok
  if (path === REFRESH_PATH) {
    emitOnce('unauthorized', 'Oturum süresi doldu. Tekrar giriş yapın.');
    const err = new Error('Unauthorized (refresh)');
    err.status = 401;
    err.data = first.data;
    throw err;
  }

  try {
    // single-flight refresh
    if (!refreshingPromise) {
      refreshingPromise = doRefresh().finally(() => {
        refreshingPromise = null;
      });
    }
    await refreshingPromise;

    // 4) retry once
    const retry = await requestOnce(path, { method, body, auth, headers });

    if (retry.res.ok) return retry.data;

    // ✅ retry sonucu 403 ise yine “Yetkiniz yok”
    if (retry.res.status === 403) {
      emitOnce('forbidden', 'Yetkiniz yok.');
      const err = new Error('FORBIDDEN');
      err.status = 403;
      err.data = retry.data;
      throw err;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.group('❌ API ERROR (after refresh)');
      console.log('URL:', retry.url);
      console.log('STATUS:', retry.res.status);
      console.log('RESPONSE:', retry.data);
      console.groupEnd();
    }

    const msg = pickMessage(retry.data, retry.res.status);
    const err = new Error(msg);
    err.status = retry.res.status;
    err.data = retry.data;
    err.errors = retry.data?.errors;
    throw err;
  } catch (e) {
    // ✅ sadece refresh fail (401/403 vb) ise logout yap
    if (e?.status === 401 || e?.status === 403 || e?.message?.includes('Refresh')) {
      tokenStorage.clear?.();
      emitOnce('unauthorized', 'Oturum süresi doldu. Tekrar giriş yapın.');
    }
    throw e;
  }
}
